import { useId, useRef, useState } from "react";
import type { Adjunto } from "@/lib/api";
import { TIPOS_ADJUNTO, formatTamanio, validarArchivo, type TiposAdjunto } from "@/lib/adjuntos";
import { Button } from "./Button";
import { Row, Stack } from "./Stack";

export interface AdjuntosProps {
  etiqueta: string;
  adjuntos: Adjunto[];
  rutaDescarga: (a: Adjunto) => string;
  puedeEditar: boolean;
  /** Sólo se usan si `puedeEditar` es true. */
  onSubir?: (archivo: File) => Promise<unknown>;
  onBorrar?: (a: Adjunto) => Promise<unknown>;
  subiendo?: boolean;
  /** Error del servidor (409 tope, 413 tamaño, 422 tipo, 503 bucket). */
  error: string | null;
  /** Qué archivos se aceptan (por defecto PDF e imágenes; `TIPOS_IMAGEN`, `TIPOS_MAQUETA`). */
  tipos?: TiposAdjunto;
}

/** Lista de archivos con descarga y, para el admin, subir y borrar. Valida tipo y tamaño antes de mandar. */
export function Adjuntos({ etiqueta, adjuntos, rutaDescarga, puedeEditar, onSubir, onBorrar, subiendo, error, tipos = TIPOS_ADJUNTO }: AdjuntosProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const idInput = useId();
  const [errorLocal, setErrorLocal] = useState<string | null>(null);
  const mensaje = errorLocal ?? error;

  const elegir = async (archivo: File | undefined) => {
    if (!archivo) return;
    const invalido = validarArchivo(archivo, tipos);
    setErrorLocal(invalido);
    if (invalido) return;
    try {
      await onSubir?.(archivo);
    } catch {
      // El consumidor (la ficha de gasto) informa el error de servidor por la prop `error`.
    } finally {
      // Siempre, aunque falle: si no se resetea, el browser no dispara "change" al reintentar con el mismo archivo.
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const borrar = async (a: Adjunto) => {
    try {
      await onBorrar?.(a);
    } catch {
      // El consumidor (la ficha de gasto) informa el error de servidor por la prop `error`.
    }
  };

  return (
    <Stack espacio={2}>
      <Row justificar="entre" alinear="centro">
        <h3 className="text-chico font-medium text-texto-suave">{etiqueta}</h3>
        {puedeEditar && (
          <>
            <input
              ref={inputRef}
              id={idInput}
              type="file"
              accept={tipos.accept}
              className="sr-only"
              aria-label={`Subir archivo a ${etiqueta}`}
              disabled={subiendo}
              onChange={(e) => void elegir(e.target.files?.[0])}
            />
            <Button variante="secundario" tamanio="sm" cargando={subiendo} onClick={() => inputRef.current?.click()}>
              Subir archivo
            </Button>
          </>
        )}
      </Row>
      {adjuntos.length === 0 ? (
        <p className="text-chico text-texto-suave">Sin archivos.</p>
      ) : (
        <ul className="divide-y divide-borde rounded-control border border-borde">
          {adjuntos.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-3 px-3 py-2 text-chico">
              <a href={rutaDescarga(a)} className="truncate text-primario underline-offset-2 hover:underline" download>
                {a.nombre}
              </a>
              <Row espacio={2}>
                <span className="whitespace-nowrap text-texto-suave">{formatTamanio(a.tamanio)}</span>
                {puedeEditar && (
                  <Button variante="fantasma" tamanio="sm" aria-label={`Borrar ${a.nombre}`} onClick={() => void borrar(a)}>
                    Borrar
                  </Button>
                )}
              </Row>
            </li>
          ))}
        </ul>
      )}
      {mensaje && (
        <p role="alert" className="rounded-control bg-peligro-suave px-3 py-2 text-chico text-peligro">
          {mensaje}
        </p>
      )}
    </Stack>
  );
}
